import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Chip, EmptyState, PageHeader, PageLoading, Stat, memberColor } from '@koyomi/ui';
import { useCalendarMembers, useWaitlist, type WaitlistQueueDto } from '../../lib/calendar-api.ts';
import { formatMoney, useBusiness, useSave } from '../../lib/queries.ts';
import { useRealtime } from '../../lib/socket.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import { api } from '../../lib/api.ts';
import { dayOf, fmtDateTime, timeOf, todayIn } from '../../lib/time.ts';
import './waitlist.css';

const MAX_CASCADE = 3;

export function WaitlistPage() {
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const businessId = useAuthStore((s) => s.businessId);
  const business = useBusiness();
  const members = useCalendarMembers();
  const list = useWaitlist();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(id); }, []);
  useRealtime({ 'waitlist.changed': () => qc.invalidateQueries({ queryKey: [businessId, 'waitlist'] }) });
  const tz = business.data?.timezone ?? 'America/Sao_Paulo';
  const queues = list.data?.queues ?? [];
  const selected = queues.find((q) => q.key === selectedKey) ?? queues.find((q) => q.offer) ?? queues[0] ?? null;
  const offers = queues.filter((q) => q.offer).length;
  const closeQueue = useSave((ids: string[]) => api('/business/waitlist/close', { body: { requestIds: ids } }), ['waitlist']);
  const remove = useSave((id: string) => api(`/business/waitlist/${id}`, { method: 'DELETE' }), ['waitlist']);
  const memberOf = (id: string | null) => members.data?.find((m) => m.id === id);
  const whenLabel = (iso: string) => (dayOf(iso, tz) === todayIn(tz) ? t('calendar:waitlist.today') : fmtDateTime(iso, tz, i18n.language, { weekday: 'short', day: '2-digit', month: '2-digit' })) + ' · ' + timeOf(iso, tz);
  const month = new Intl.DateTimeFormat(i18n.language, { month: 'long', timeZone: tz }).format(new Date());
  const holdMinutes = business.data?.settings.waitlist?.holdMinutes ?? 30;

  if (list.isLoading || business.isLoading || members.isLoading) return <><PageHeader title={t('calendar:waitlist.title')} /><PageLoading cards={2} /></>;
  return (
    <>
      <PageHeader title={t('calendar:waitlist.title')} subtitle={queues.length === 0 ? t('calendar:waitlist.summary_zero') : t('calendar:waitlist.summary', { queues: queues.length, offers, count: offers })} />
      {list.data && queues.length === 0 ? (
        <EmptyState title={t('common:status.empty')} description={t('calendar:waitlist.empty')} />
      ) : (
        <div className="wl">
          <div>
            <div className="wl__list">
              {queues.map((q) => {
                const m = memberOf(q.memberId);
                return (
                  <button key={q.key} type="button" className={`wl__card ${selected?.key === q.key ? 'wl__card--active' : ''}`} onClick={() => setSelectedKey(q.key)}>
                    <div className="wl__card-row">
                      <span className="wl__card-title"><span className="wl__dot" style={{ background: memberColor(m?.colorIndex ?? 0).strong }} />{whenLabel(q.desiredStart)}</span>
                      <span className="wl__count">{t('calendar:waitlist.inQueue', { count: q.count })}</span>
                    </div>
                    <div className="wl__sub">{q.serviceNames.join(' + ')}{m ? ` · ${m.name}` : ''}</div>
                    <div className={`wl__state ${q.offer ? 'wl__state--offer' : ''}`}>{q.offer ? t(q.offer.status === 'PENDING' ? 'calendar:waitlist.card.holdOffer' : 'calendar:waitlist.card.softOffer') : t('calendar:waitlist.card.waiting')}</div>
                  </button>
                );
              })}
            </div>
            <div className="wl__stat">
              <Stat label={t('calendar:waitlist.recovered.label', { month })} value={t('calendar:waitlist.recovered.value', { count: list.data?.recovered.count ?? 0, amount: formatMoney(list.data?.recovered.value ?? 0, i18n.language) })} hint={t('calendar:waitlist.recovered.hint')} />
            </div>
          </div>
          {selected && <QueueDetail q={selected} tz={tz} now={now} holdMinutes={holdMinutes} memberName={memberOf(selected.memberId)?.name ?? ''} onClose={() => { if (confirm(t('calendar:waitlist.detail.closeConfirm'))) closeQueue.mutate(selected.entries.map((e) => e.requestId)); }} onRemove={(id) => { if (!remove.isPending && confirm(t('calendar:waitlist.detail.removeConfirm'))) remove.mutate(id); }} onCalendar={() => navigate(`/agenda?day=${dayOf(selected.desiredStart, tz)}`)} />}
        </div>
      )}
    </>
  );
}

function QueueDetail({ q, tz, now, holdMinutes, memberName, onClose, onRemove, onCalendar }: { q: WaitlistQueueDto; tz: string; now: number; holdMinutes: number; memberName: string; onClose: () => void; onRemove: (id: string) => void; onCalendar: () => void }) {
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const o = q.offer;
  const isToday = dayOf(q.desiredStart, tz) === todayIn(tz);
  const when = (isToday ? t('calendar:waitlist.today') : fmtDateTime(q.desiredStart, tz, i18n.language, { weekday: 'short', day: '2-digit', month: '2-digit' })) + ' · ' + timeOf(q.desiredStart, tz);
  const timeline = useMemo(() => {
    if (!o) return null;
    const start = new Date(o.createdAt).getTime(), hold = new Date(o.holdExpiresAt).getTime(), end = new Date(o.offerWindowEndsAt).getTime();
    const span = Math.max(end - start, 1);
    const pct = (x: number) => `${Math.min(100, Math.max(0, ((x - start) / span) * 100))}%`;
    const untilSlot = new Date(o.slotStart).getTime() - start;
    const rule = untilSlot > 24 * 3600_000 ? 'rule_far' : untilSlot > 4 * 3600_000 ? 'rule_sameDay' : 'rule_near';
    const nowP = Math.min(100, Math.max(0, ((now - start) / span) * 100));
    return { holdPct: pct(hold), nowPct: `${nowP}%`, nowAlign: nowP < 12 ? 'left' : nowP > 88 ? 'right' : 'center', rule, holdLeft: Math.max(0, Math.round((hold - now) / 60_000)), multiDay: dayOf(o.offerWindowEndsAt, tz) !== dayOf(o.createdAt, tz) };
  }, [o, now, tz]);
  const offerEntry = o ? q.entries.find((e) => e.requestId === o.requestId) : null;
  const next = offerEntry ? q.entries.find((e) => e.position === offerEntry.position + 1) : null;
  const first = (name: string) => name.split(' ')[0] ?? name;
  return (
    <div className="wl__detail">
      <div className="wl__detail-head">
        <div>
          <div className="k-section-label">{t(o ? 'calendar:waitlist.detail.released' : 'calendar:waitlist.detail.occupied')}</div>
          <h2 className="wl__detail-title">{when} · {q.serviceNames.join(' + ')}</h2>
          <div className="wl__detail-sub">{memberName}{memberName ? ' · ' : ''}{o ? (o.releasedFromClientName ? t('calendar:waitlist.detail.releasedBy', { time: timeOf(o.createdAt, tz), name: o.releasedFromClientName }) : t('calendar:waitlist.detail.releasedAt', { time: timeOf(o.createdAt, tz) })) : t('calendar:waitlist.detail.waitingHint')}</div>
        </div>
        <div className="k-row">
          <Button onClick={onClose}>{t('calendar:waitlist.detail.closeQueue')}</Button>
          <Button onClick={onCalendar}>{t('calendar:waitlist.detail.viewCalendar')}</Button>
        </div>
      </div>
      {o && timeline && (
        <Card>
          <div className="wl__timeline-head"><strong>{t('calendar:waitlist.detail.timeline')}</strong><span>{t(`calendar:waitlist.detail.${timeline.rule}`)}</span></div>
          <div className="wl__bar-wrap">
            <div className="wl__bar"><div className={`wl__bar-hold ${o.status === 'SOFT' ? 'wl__bar-hold--soft' : ''}`} style={{ width: timeline.holdPct, minWidth: '3%' }} /></div>
            <div className="wl__now" style={{ left: timeline.nowPct }}><span className={`wl__now-label wl__now-label--${timeline.nowAlign}`}>{t('calendar:waitlist.detail.now', { time: timeOf(new Date(now), tz) })}</span></div>
          </div>
          <div className="wl__marks">
            <span>{t('calendar:waitlist.detail.sent', { time: timeOf(o.createdAt, tz) })}</span>
            <span>{t('calendar:waitlist.detail.holdEnd', { time: timeOf(o.holdExpiresAt, tz) })}</span>
            <span>{t('calendar:waitlist.detail.windowEnd', { time: timeline.multiDay ? fmtDateTime(o.offerWindowEndsAt, tz, i18n.language) : timeOf(o.offerWindowEndsAt, tz) })}</span>
          </div>
          <div className="wl__legend">
            <div className="wl__legend-item"><span className="wl__swatch" /><span><Trans i18nKey="calendar:waitlist.detail.holdLegend" values={{ minutes: holdMinutes, name: first(offerEntry?.clientName ?? '') }} components={{ b: <b /> }} /></span></div>
            <div className="wl__legend-item"><span className="wl__swatch wl__swatch--soft" /><span>{next ? <Trans i18nKey="calendar:waitlist.detail.softLegend" values={{ next: first(next.clientName), time: timeline.multiDay ? fmtDateTime(o.offerWindowEndsAt, tz, i18n.language) : timeOf(o.offerWindowEndsAt, tz) }} components={{ b: <b /> }} /> : <Trans i18nKey="calendar:waitlist.detail.softLegendNoNext" values={{ time: timeline.multiDay ? fmtDateTime(o.offerWindowEndsAt, tz, i18n.language) : timeOf(o.offerWindowEndsAt, tz) }} components={{ b: <b /> }} />}</span></div>
          </div>
        </Card>
      )}
      <div>
        <div className="k-section-label" style={{ marginBottom: 12 }}>{t('calendar:waitlist.detail.order')}</div>
        <div className="k-list">
          {q.entries.map((e) => {
            const hasOffer = o?.requestId === e.requestId;
            return (
              <div key={e.requestId} className={`wl__entry ${hasOffer ? 'wl__entry--offer' : ''}`}>
                <div className="wl__pos">{e.position}</div>
                <div className="wl__entry-body">
                  <div className="wl__entry-name">
                    <Link to={`/clientes/${e.clientId}`}>{e.clientName}</Link>
                    {hasOffer && o && (o.status === 'PENDING' ? <Chip tone="warn">{t('calendar:waitlist.detail.holdLeft', { minutes: timeline?.holdLeft ?? 0 })}</Chip> : <Chip tone="neutral">{t('calendar:waitlist.detail.soft', { time: timeOf(o.offerWindowEndsAt, tz) })}</Chip>)}
                  </div>
                  {hasOffer && o ? (
                    <div className="wl__entry-meta">{t('calendar:waitlist.detail.offerSent', { time: timeOf(o.createdAt, tz) })}{o.message?.deliveredAt || o.message?.deliveryStatus === 'DELIVERED' || o.message?.deliveryStatus === 'READ' ? ` · ${t('calendar:waitlist.detail.delivered')}` : ''}{o.message?.readAt ? ` · ${t('calendar:waitlist.detail.read', { time: timeOf(o.message.readAt, tz) })}` : ''}{o.message?.deliveryStatus === 'FAILED' ? ` · ${t('calendar:waitlist.detail.failed')}` : ''}</div>
                  ) : (
                    <div className="wl__entry-meta">{t('calendar:waitlist.detail.joined', { date: fmtDateTime(e.joinedAt, tz, i18n.language, { day: '2-digit', month: '2-digit' }) })}{e.otherQueues > 0 ? ` · ${t('calendar:waitlist.detail.otherQueues', { count: e.otherQueues })}` : ''}</div>
                  )}
                  {e.replacesAppointmentStart && <div className="wl__entry-meta">{t('calendar:waitlist.detail.cascade', { when: fmtDateTime(e.replacesAppointmentStart, tz, i18n.language, { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }), level: e.cascadeLevel + 1, max: MAX_CASCADE })}</div>}
                </div>
                <div className="wl__entry-side">
                  {!hasOffer && <span>{t('calendar:waitlist.detail.waitingChip')}</span>}
                  <Button size="sm" variant="ghost" onClick={() => onRemove(e.requestId)} aria-label={t('calendar:waitlist.detail.remove')}>×</Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
